/**
 * Generic, columns-driven list popup used by role Insights cards so that
 * "card count === popup rows" is guaranteed (the popup is always rendered
 * from the SAME filtered dataset the card was counted from).
 */
import React from 'react';
import { X, ExternalLink } from 'lucide-react';

const OVERDUE_PALETTE = {
  Late:     'bg-amber-100 text-amber-700 border-amber-200',
  Urgent:   'bg-orange-100 text-orange-700 border-orange-200',
  Critical: 'bg-rose-100 text-rose-700 border-rose-200',
};

const InsightListPopup = ({ title, columns, rows, onClose, testId = 'insight-list-popup' }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid={testId}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[80vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
          <div>
            <h3 className="font-bold text-gray-800 text-base">{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5" data-testid={`${testId}-count`}>{rows.length} row{rows.length === 1 ? '' : 's'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg" data-testid={`${testId}-close`} aria-label="Close">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {rows.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-12">No records for this card with the current filters.</p>
          ) : (
            <table className="w-full text-sm" data-testid={`${testId}-table`}>
              <thead className="bg-gray-50 sticky top-0 text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className="px-4 py-2.5 text-left font-semibold whitespace-nowrap">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, i) => (
                  <tr key={`${row.lead_id || i}-${row.ota_index ?? i}`} className="hover:bg-gray-50" data-testid={`${testId}-row-${i}`}>
                    {columns.map((c) => {
                      const v = row[c.key];
                      if (c.key === 'overdue_label' || c.key === 'status_badge') {
                        const s = String(v ?? '').trim();
                        return (
                          <td key={c.key} className="px-4 py-2.5 whitespace-nowrap">
                            {s ? <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${OVERDUE_PALETTE[s] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>{s}</span> : <span className="text-gray-300">—</span>}
                          </td>
                        );
                      }
                      if (c.key === 'listing_url') {
                        return (
                          <td key={c.key} className="px-4 py-2.5 whitespace-nowrap">
                            {v ? <a href={v} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline inline-flex items-center gap-1"><ExternalLink size={12} /> open</a> : '—'}
                          </td>
                        );
                      }
                      return (
                        <td key={c.key} className="px-4 py-2.5 text-gray-700 whitespace-nowrap max-w-xs truncate" title={String(v ?? '')}>
                          {v ?? '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsightListPopup;
